import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import { authService } from '@/services/auth.service';
import { clearTokens, getAccessToken } from '@/services/api';
import type { ILoginRequest } from '@/types';
import { UserRole } from '@/types';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    user,
    isAuthenticated,
    isLoading,
    setUser,
    clearUser,
    setLoading,
    hasRole,
    hasAnyRole,
    hasPermission,
  } = useAuthStore();

  // Fetch current user on mount if token exists
  const { isLoading: isFetchingUser } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const userData = await authService.me();
      setUser(userData);
      return userData;
    },
    enabled: !!getAccessToken() && !user,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const loginMutation = useMutation({
    mutationFn: (credentials: ILoginRequest) => authService.login(credentials),
    onSuccess: (response) => {
      if (!response.requiresTwoFactor) {
        setUser(response.user);
        // Redirect based on role
        const dashboardRoute = getDashboardRoute(response.user.role);
        navigate(dashboardRoute);
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      clearUser();
      clearTokens();
      queryClient.clear();
      navigate('/login');
    },
  });

  const login = useCallback(
    (credentials: ILoginRequest) => loginMutation.mutateAsync(credentials),
    [loginMutation]
  );

  const logout = useCallback(
    () => logoutMutation.mutateAsync(),
    [logoutMutation]
  );

  const initialize = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const userData = await authService.me();
      setUser(userData);
    } catch {
      clearUser();
      clearTokens();
    }
  }, [setUser, clearUser, setLoading]);

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || isFetchingUser,
    login,
    logout,
    initialize,
    loginError: loginMutation.error,
    isLoginLoading: loginMutation.isPending,
    hasRole,
    hasAnyRole,
    hasPermission,
  };
}

function getDashboardRoute(role: string): string {
  switch (role) {
    case UserRole.SYSADMIN:
    case UserRole.ASSET_ADMIN:
      return '/dashboard/operativo';
    case UserRole.ACCOUNTANT:
      return '/dashboard/contable';
    case UserRole.AUDITOR:
      return '/dashboard/ejecutivo';
    default:
      return '/dashboard/operativo';
  }
}
